/* Contacts — vlakke lijst van alle contactpersonen over companies heen.
 * Card view + Lijst (excel-achtig) + multiselect bulk-delete.
 * Bron: GET /api/contacts (primaire contacten + stakeholders uit leads). */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { toast } from "sonner";
import {
    Users, Search, Loader2, Trash2, Linkedin, Mail, Phone, Building2,
    LayoutGrid, List as ListIcon, ChevronUp, ChevronDown, ChevronsUpDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import ContactDrawer from "@/components/ContactDrawer";

const KIND_CLS = {
    primary: "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/40",
    stakeholder: "bg-[var(--jr-info)]/15 text-[var(--jr-info)] border-[var(--jr-info)]/40",
};

function Checkbox({ checked, onClick, testid, label }) {
    return (
        <button onClick={onClick} data-testid={testid} aria-label={label} title={label}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${checked ? "bg-[var(--jr-brand)] border-[var(--jr-brand)]" : "border-white/25 hover:border-white/50"}`}>
            {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </button>
    );
}

function SortTh({ label, sortKey, sort, onSort, testid }) {
    const active = sort.key === sortKey;
    return (
        <th className="px-3 py-2.5 select-none text-left">
            <button onClick={() => onSort(sortKey)} data-testid={testid}
                className={`inline-flex items-center gap-1 uppercase tracking-[0.14em] text-[10px] font-mono transition-colors ${active ? "text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                {label}
                {active ? (sort.dir === 1 ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : <ChevronsUpDown size={11} className="opacity-40" />}
            </button>
        </th>
    );
}

const initialsOf = (name) => (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export default function Contacts() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [view, setView] = useState("list");           // "list" | "cards"
    const [kindFilter, setKindFilter] = useState("");   // "" | primary | stakeholder
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [sort, setSort] = useState({ key: "name", dir: 1 });
    const [drawerRef, setDrawerRef] = useState(null);

    const openDrawer = (r) => setDrawerRef({
        kind: r.kind, leadId: r.lead_id,
        ...(r.stakeholder_id ? { contactId: r.stakeholder_id } : {}),
    });

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/contacts"); setRows(r.data.contacts || []); }
        catch { toast.error(t("contacts.load_failed")); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        let arr = rows;
        if (kindFilter) arr = arr.filter((r) => r.kind === kindFilter);
        if (search.trim()) {
            const q = search.toLowerCase();
            arr = arr.filter((r) => `${r.name} ${r.company_name} ${r.title} ${r.email || ""}`.toLowerCase().includes(q));
        }
        const { key, dir } = sort;
        return [...arr].sort((a, b) => {
            const va = (a[key] || "").toString().toLowerCase();
            const vb = (b[key] || "").toString().toLowerCase();
            return va < vb ? -dir : va > vb ? dir : 0;
        });
    }, [rows, search, kindFilter, sort]);

    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
    const toggleSelect = (key) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });
    const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.key));
    const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((r) => r.key)));

    const bulkDelete = async () => {
        if (!window.confirm(t("contacts.bulk_delete_confirm", { n: selected.size }))) return;
        setBulkBusy(true);
        try {
            const items = rows.filter((r) => selected.has(r.key))
                .map((r) => ({ lead_id: r.lead_id, kind: r.kind, stakeholder_id: r.stakeholder_id }));
            const { data } = await api.post("/contacts/bulk-delete", { items: items.slice(0, 200) });
            toast.success(t("contacts.bulk_deleted_toast", { n: data.deleted }));
            setSelected(new Set());
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("contacts.delete_failed")); }
        finally { setBulkBusy(false); }
    };

    const kindBadge = (r) => (
        <span className={`text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${KIND_CLS[r.kind]}`}>
            {r.kind === "primary" ? t("contacts.kind_primary") : t("contacts.kind_stakeholder")}
        </span>
    );

    const bulkBar = selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/[0.07] mb-4"
            data-testid="contacts-bulk-bar">
            <span className="text-xs text-[var(--jr-text-2)]">{t("contacts.selected_n", { n: selected.size })}</span>
            <button onClick={bulkDelete} disabled={bulkBusy} data-testid="contacts-bulk-delete"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 disabled:opacity-50">
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                {t("contacts.bulk_delete", { n: selected.size })}
            </button>
            <button onClick={() => setSelected(new Set())} data-testid="contacts-bulk-clear"
                className="ml-auto text-xs text-[var(--jr-text-3)] hover:text-white">{t("common.cancel")}</button>
        </div>
    );

    return (
        <div data-testid="contacts-page">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{t("contacts.title")}</h1>
                    <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("contacts.subtitle")}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* View toggle */}
                    <div className="inline-flex rounded-full border border-white/10 p-0.5">
                        <button onClick={() => setView("list")} data-testid="contacts-view-list"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] ${view === "list" ? "bg-white/10 text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                            <ListIcon size={12} /> {t("contacts.view_list")}
                        </button>
                        <button onClick={() => setView("cards")} data-testid="contacts-view-cards"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] ${view === "cards" ? "bg-white/10 text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                            <LayoutGrid size={12} /> {t("contacts.view_cards")}
                        </button>
                    </div>
                    <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="contacts-kind-filter"
                        className="bg-[var(--jr-surface)] border border-white/10 rounded-full px-3 py-1.5 text-xs text-[var(--jr-text-2)] focus:outline-none">
                        <option value="">{t("contacts.all_kinds")}</option>
                        <option value="primary">{t("contacts.kind_primary")}</option>
                        <option value="stakeholder">{t("contacts.kind_stakeholder")}</option>
                    </select>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("contacts.search_ph")}
                            data-testid="contacts-search"
                            className="bg-[var(--jr-surface)] border border-white/10 rounded-full pl-9 pr-4 py-1.5 text-sm w-56 focus:outline-none focus:border-[var(--jr-brand)]/50" />
                    </div>
                </div>
            </div>

            {bulkBar}

            {loading ? (
                <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-[var(--jr-brand)]" /></div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-10 text-center text-sm text-[var(--jr-text-3)]">
                    <Users size={24} className="mx-auto mb-3 opacity-50" />
                    {t("contacts.empty")}
                </div>
            ) : view === "list" ? (
                /* ============== LIST VIEW (excel-achtig) ============== */
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-white/8 bg-white/[0.02]">
                                    <th className="pl-4 pr-1 py-2.5 w-8">
                                        <Checkbox checked={allSelected} onClick={toggleSelectAll}
                                            testid="contacts-select-all" label={t("contacts.select_all")} />
                                    </th>
                                    <SortTh label={t("contacts.col_name")} sortKey="name" sort={sort} onSort={toggleSort} testid="sort-name" />
                                    <SortTh label={t("contacts.col_title")} sortKey="title" sort={sort} onSort={toggleSort} testid="sort-title" />
                                    <SortTh label={t("contacts.col_company")} sortKey="company_name" sort={sort} onSort={toggleSort} testid="sort-company" />
                                    <SortTh label={t("contacts.col_kind")} sortKey="kind" sort={sort} onSort={toggleSort} testid="sort-kind" />
                                    <th className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] text-left">{t("contacts.col_channels")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const isSel = selected.has(r.key);
                                    return (
                                        <tr key={r.key} data-testid={`contact-row-${r.key}`}
                                            className={`border-b border-white/5 transition-colors ${isSel ? "bg-[var(--jr-brand)]/[0.06]" : "hover:bg-white/[0.02]"}`}>
                                            <td className="pl-4 pr-1 py-2.5 w-8">
                                                <Checkbox checked={isSel} onClick={() => toggleSelect(r.key)}
                                                    testid={`contact-select-${r.key}`} label={t("contacts.select_one")} />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div className="w-6 h-6 rounded-full bg-[var(--jr-surface-2)] grid place-items-center text-[9px] font-mono uppercase text-[var(--jr-text-2)] shrink-0">
                                                        {initialsOf(r.name)}
                                                    </div>
                                                    <span className="text-white font-medium truncate max-w-[200px] cursor-pointer hover:text-[var(--jr-brand)] hover:underline underline-offset-2 transition-colors"
                                                        title={r.name} onClick={() => openDrawer(r)}
                                                        data-testid={`open-contact-${r.key}`}>{r.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-xs text-[var(--jr-text-2)] truncate max-w-[200px]" title={r.title}>{r.title || "—"}</td>
                                            <td className="px-3 py-2.5">
                                                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-text-2)]">
                                                    <Building2 size={11} className="text-[var(--jr-text-3)]" /> {r.company_name || "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5">{kindBadge(r)}</td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-1">
                                                    <LinkedInButton url={r.linkedin_url} name={r.name} variant="icon" testid={`contact-li-${r.key}`} />
                                                    {r.email && (
                                                        <a href={`mailto:${r.email}`} title={r.email} data-testid={`contact-email-${r.key}`}
                                                            className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8"><Mail size={13} /></a>
                                                    )}
                                                    {r.phone && (
                                                        <a href={`tel:${r.phone}`} title={r.phone}
                                                            className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8"><Phone size={13} /></a>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                /* ============== CARD VIEW ============== */
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filtered.map((r) => {
                        const isSel = selected.has(r.key);
                        return (
                            <div key={r.key} data-testid={`contact-card-${r.key}`}
                                className={`rounded-xl border p-4 transition-colors ${isSel ? "border-[var(--jr-brand)]/50 bg-[var(--jr-brand)]/[0.06]" : "border-white/8 bg-[var(--jr-surface)] hover:border-white/14"}`}>
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-[var(--jr-surface-2)] grid place-items-center text-xs font-mono uppercase text-[var(--jr-text-2)]">
                                        {initialsOf(r.name)}
                                    </div>
                                    <Checkbox checked={isSel} onClick={() => toggleSelect(r.key)}
                                        testid={`contact-card-select-${r.key}`} label={t("contacts.select_one")} />
                                </div>
                                <div className="text-sm font-medium text-white truncate cursor-pointer hover:text-[var(--jr-brand)] hover:underline underline-offset-2 transition-colors"
                                    title={r.name} onClick={() => openDrawer(r)}
                                    data-testid={`open-contact-card-${r.key}`}>{r.name}</div>
                                <div className="text-xs text-[var(--jr-text-2)] truncate mt-0.5" title={r.title}>{r.title || "—"}</div>
                                <div className="flex items-center gap-1.5 text-xs text-[var(--jr-text-3)] mt-1 truncate">
                                    <Building2 size={11} /> {r.company_name || "—"}
                                </div>
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/8">
                                    {kindBadge(r)}
                                    <div className="flex items-center gap-1">
                                        <LinkedInButton url={r.linkedin_url} name={r.name} variant="icon" testid={`contact-card-li-${r.key}`} />
                                        {r.email && (
                                            <a href={`mailto:${r.email}`} title={r.email}
                                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8"><Mail size={13} /></a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <ContactDrawer contactRef={drawerRef} onClose={() => setDrawerRef(null)} onChanged={load} />
        </div>
    );
}
